import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, IndianRupee, Car, Gauge, Users, IdCard, LifeBuoy, Undo2, ArrowUpRight, ArrowDownRight, Inbox } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useApi } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { StatusBadge } from "@/components/site/Shared";
import { ExpiryAlerts } from "@/components/admin/ExpiryAlerts";

const RANGES = [[7, "Last 7 days"], [30, "Last 30 days"], [90, "Last 90 days"], [365, "Last 12 months"]];
const STATUS_COLORS = [["available", "Available", "#16834A"], ["on_trip", "On trip", "#0F5132"], ["in_service", "Service due", "#E9A23B"], ["inactive", "Inactive", "#B8C2BA"]];
const ddmm = (iso) => iso.slice(8, 10) + "/" + iso.slice(5, 7);

function Kpi({ icon: Icon, label, value, change, testId }) {
  const up = change >= 0;
  return (
    <div className="card-hc p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-hc-mint"><Icon className="h-5 w-5 text-hc-green" /></span>
        {change !== undefined && <span className={`flex items-center gap-0.5 text-xs font-semibold ${up ? "text-hc-success" : "text-hc-error"}`}>{up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}{Math.abs(change)}%</span>}
      </div>
      <p className="mt-3 text-xs font-medium text-hc-slate">{label}</p>
      <p className="tabular font-display text-2xl font-extrabold text-hc-ink" data-testid={`${testId}-value`}>{value}</p>
    </div>
  );
}

function Mini({ icon: Icon, label, value, to, testId }) {
  return (
    <Link to={to} className="flex items-center justify-between rounded-xl border border-hc-sage bg-white px-4 py-3 transition-colors duration-200 hover:border-hc-green" data-testid={testId}>
      <span className="flex items-center gap-2.5 text-sm text-hc-ink"><Icon className="h-4 w-4 text-hc-goldh" />{label}</span>
      <span className="tabular font-bold text-hc-ink">{value}</span>
    </Link>
  );
}

function TrendChart({ series }) {
  return (
    <div className="card-hc p-5 lg:col-span-2" data-testid="dashboard-trend">
      <h2 className="font-display text-lg font-bold">Bookings & revenue trend</h2>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ left: -10, right: 8, top: 8 }}>
            <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16834A" stopOpacity={0.35} /><stop offset="100%" stopColor="#16834A" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid stroke="#E3EAE3" vertical={false} />
            <XAxis dataKey="date" tickFormatter={ddmm} tick={{ fontSize: 11, fill: "#68736C" }} minTickGap={24} />
            <YAxis yAxisId="b" allowDecimals={false} tick={{ fontSize: 11, fill: "#68736C" }} />
            <YAxis yAxisId="r" orientation="right" hide />
            <Tooltip labelFormatter={(d) => d.split("-").reverse().join("/")} formatter={(v, n) => (n === "revenue" ? inr(v) : v)} />
            <Area yAxisId="b" type="monotone" dataKey="bookings" stroke="#0F5132" strokeWidth={2} fill="url(#g1)" />
            <Area yAxisId="r" type="monotone" dataKey="revenue" stroke="#D4AF67" strokeWidth={2} fill="none" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 flex gap-4 text-xs text-hc-slate"><span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-hc-green" />Bookings</span><span className="flex items-center gap-1.5"><i className="h-2 w-2 rounded-full bg-hc-gold" />Revenue</span></p>
    </div>
  );
}

function VehicleStatus({ vs }) {
  const data = STATUS_COLORS.map(([k, l, c]) => ({ name: l, value: vs[k], color: c }));
  return (
    <div className="card-hc p-5" data-testid="dashboard-vehicle-status">
      <h2 className="font-display text-lg font-bold">Vehicle status</h2>
      <div className="relative mx-auto mt-2 h-44 w-44">
        <ResponsiveContainer><PieChart><Pie data={data.some((d) => d.value) ? data : [{ value: 1, color: "#E3EAE3" }]} dataKey="value" innerRadius={55} outerRadius={78} paddingAngle={2} stroke="none">
          {(data.some((d) => d.value) ? data : [{ color: "#E3EAE3" }]).map((d, i) => <Cell key={i} fill={d.color} />)}</Pie></PieChart></ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center text-center"><div><p className="tabular font-display text-3xl font-extrabold" data-testid="vehicle-total">{vs.total}</p><p className="text-xs text-hc-slate">Total</p></div></div>
      </div>
      <ul className="mt-3 space-y-2 text-sm">
        {data.map((d) => <li key={d.name} className="flex items-center justify-between"><span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />{d.name}</span><b className="tabular">{d.value}</b></li>)}
      </ul>
    </div>
  );
}

function Breakdown({ title, rows, testId }) {
  return (
    <div className="card-hc p-5" data-testid={testId}>
      <h2 className="font-display text-lg font-bold">{title}</h2>
      {rows.length === 0 ? <p className="mt-6 text-sm text-hc-slate">No bookings in this period.</p> : (
        <div className="mt-3 h-48"><ResponsiveContainer><BarChart data={rows} layout="vertical" margin={{ left: 10 }}>
          <XAxis type="number" hide allowDecimals={false} /><YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: "#17231C" }} />
          <Tooltip /><Bar dataKey="value" fill="#0F5132" radius={[0, 6, 6, 0]} barSize={16} />
        </BarChart></ResponsiveContainer></div>
      )}
    </div>
  );
}

function Recent({ recent }) {
  return (
    <div className="card-hc p-5 lg:col-span-2" data-testid="dashboard-recent">
      <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold">Recent bookings</h2><Link to="/admin/bookings" className="text-sm font-semibold text-hc-green" data-testid="dashboard-view-bookings">View all</Link></div>
      <div className="mt-3 divide-y divide-hc-sage">
        {recent.length === 0 && <p className="py-6 text-sm text-hc-slate">No bookings yet.</p>}
        {recent.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
            <div><p className="font-medium">{b.car_name} · {b.customer_name}</p><p className="text-xs text-hc-slate">{b.id} · {fmtDT(b.pickup_at)}</p></div>
            <div className="flex items-center gap-3"><span className="tabular font-semibold">{inr(b.quote.total)}</span><StatusBadge status={b.status} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const api = useApi();
  const [days, setDays] = useState(30);
  const [a, setA] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setErr(false);
    api.get("/admin/analytics", { params: { days } }).then((r) => setA(r.data)).catch(() => setErr(true));
  }, [api, days]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setRecent(r.data.recent)).catch(() => {});
  }, [api]);
  if (err) return <p className="text-hc-error" data-testid="dashboard-error">Could not load the dashboard. Refresh to try again.</p>;
  if (!a) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{[1, 2, 3, 4].map((i) => <div key={i} className="skeleton h-32" />)}</div>;
  const k = a.kpis;
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Dashboard</h1>
        <select value={days} onChange={(e) => { setA(null); setDays(Number(e.target.value)); }} className="rounded-xl border border-hc-sage bg-white px-3 py-2 text-sm" data-testid="dashboard-range">
          {RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={CalendarCheck} label="Total bookings" value={k.bookings} change={k.bookings_change} testId="kpi-bookings" />
        <Kpi icon={IndianRupee} label="Revenue (excl. deposits)" value={inr(k.revenue)} change={k.revenue_change} testId="kpi-revenue" />
        <Kpi icon={Car} label="Active rentals" value={k.active_rentals} testId="kpi-active" />
        <Kpi icon={Gauge} label="Fleet utilisation" value={`${k.utilization}%`} testId="kpi-utilization" />
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Mini icon={CalendarCheck} label="Upcoming pickups" value={k.upcoming} to="/admin/bookings" testId="mini-upcoming" />
        <Mini icon={IdCard} label="Pending KYC" value={k.pending_kyc} to="/admin/verifications" testId="mini-kyc" />
        <Mini icon={LifeBuoy} label="Open tickets" value={k.open_tickets} to="/admin/tickets" testId="mini-tickets" />
        <Mini icon={Undo2} label="Refunds pending" value={inr(k.refunds_pending)} to="/admin/bookings" testId="mini-refunds" />
        <Mini icon={Users} label="New customers" value={k.new_customers} to="/admin/customers" testId="mini-customers" />
        <Mini icon={Inbox} label="New enquiries" value={k.new_enquiries} to="/admin/enquiries" testId="mini-enquiries" />
        <Mini icon={IndianRupee} label="Gross booking value" value={inr(k.gbv)} to="/admin/reports" testId="mini-gbv" />
        <Mini icon={CalendarCheck} label="Completed / cancelled" value={`${k.completed} / ${k.cancelled}`} to="/admin/bookings" testId="mini-completed" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <TrendChart series={a.series} />
        <VehicleStatus vs={a.vehicle_status} />
      </div>
      <ExpiryAlerts />
      <div className="grid gap-4 lg:grid-cols-3">
        <Recent recent={recent} />
        <div className="space-y-4">
          <Breakdown title="By category" rows={a.by_category} testId="dashboard-by-category" />
          <Breakdown title="By city" rows={a.by_city} testId="dashboard-by-city" />
        </div>
      </div>
    </div>
  );
}
